import { useState, useRef, useEffect } from "react";
import { Drawer, Button, Typography, message, Flex, Form } from "antd";
import { EditOutlined, PrinterOutlined, SaveOutlined } from "@ant-design/icons";
import "react-quill/dist/quill.snow.css";
import { MySelect } from "../../Forms";

import { services as api } from "../../../services";
import { QuillEditor } from "../../ui";
import { useReactToPrint } from "react-to-print";

// import { ReadyToPrint } from "../ReadyToPrint";

const { Title } = Typography;

const AddEditConsentFormDrawer = ({ visible, onClose, editItem, call, id }) => {
  const printRef = useRef();
  const [form] = Form.useForm();
  const [content, setContent] = useState("");
  const [servicesLookup, setServicesLookup] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Consent Form`,
    pageStyle: `
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
        `,
  });
  useEffect(() => {
    if (api) getServicesLookUp();
  }, [api]);
  const getServicesLookUp = async () => {
    const result = await api?.servicesService?.getServicesLookUp();
    setServicesLookup(result);
  };

  useEffect(() => {
    if (editItem) {
      setContent(editItem.instruction || "");
      setSelectedServices(editItem.serviceIDs || []);
      form.setFieldsValue({
        services: editItem.serviceIDs || [],
      });
    } else {
      form.resetFields();
      setContent("");
      setSelectedServices([]);
    }
  }, [editItem, visible]);

  const AddEditConsentForm = async () => {
    const payload = {
      serviceIDs: id ? [Number(id)] : selectedServices,
      sessionIDs: [],
      instruction: content,
    };
    setLoading(true);
    const result = await api?.consentForm?.addEditConsentForm(
      payload,
      editItem?.id,
    );

    if (result?.success) {
      message.success(
        `Consent form instructions ${
          editItem?.id ? "updated" : "saved"
        } successfully!`,
      );
      onClose();
      setContent("");
      setSelectedServices([]);
      form.resetFields();
      call();
    } else {
      message.error(
        result?.message ||
          `Failed to ${editItem?.id ? "update" : "save"} pre-session instructions`,
      );
    }
    setLoading(false);
  };

  const handlePrint = () => {
    if (!componentRef.current) {
      message.error("Consent form not ready");
      return;
    }
    reactToPrintFn();
  };

  return (
    <Drawer
      title={
        <Title level={4} style={{ margin: 0 }}>
          {editItem
            ? "Edit Consent Form Instructions"
            : "Consent Form Instructions"}
        </Title>
      }
      placement="right"
      width={900}
      open={visible}
      onClose={onClose}
      closeIcon={null}
      footer={[
        <Flex justify="space-between" gap={10}>
          <Button
            style={{ background: "#2F80ED" }}
            className="text-white border-0"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={!content}
          >
            Print
          </Button>
          <Flex gap={10}>
            <Button
              type="primary"
              icon={editItem ? <EditOutlined /> : <SaveOutlined />}
              onClick={AddEditConsentForm}
              loading={loading}
            >
              {editItem ? "Update" : "Save"}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Flex>
        </Flex>,
      ]}
    >
      {/* Main Content Area */}
      <div>
        {!id && (
          <Form layout="vertical" form={form}>
            <Flex gap={8}>
              <MySelect
                mode="multiple"
                label="Services"
                name={"services"}
                allowClear
                placeholder="Select services..."
                onChange={(value) => setSelectedServices(value)}
                options={servicesLookup}
                style={{ width: "100%" }}
                filterOption={(input, option) => {
                  const labelText = String(
                    option?.children ?? "",
                  ).toLowerCase();
                  const cleanedInput = input
                    .toLowerCase()
                    .replace(/\s+/g, " ")
                    .trimEnd();

                  return labelText.includes(cleanedInput);
                }}
              />
            </Flex>
          </Form>
        )}
        <div>
          <QuillEditor
            value={content}
            onChange={setContent}
            height="calc(100vh - 200px)"
          />
        </div>
        <div style={{ display: "none" }}>
          <div ref={componentRef} className="ql-editor">
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export { AddEditConsentFormDrawer };
